import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Paper,
  Button,
  Typography,
  LinearProgress,
  CircularProgress
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useContext } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { BusinessContext } from '../../../context/BusinessContext';
import api from '../../../utils/api';
import useThemeStyles from '../../../theme/useThemeStyles';
import Step1Account from './Step1Account';
import Step2Verification from './Step2Verification';
import Step3BusinessBasics from './Step3BusinessBasics';
import Step4Location from './Step4Location';
import Step5Style from './Step5Style';
import Step6Pricing from './Step6Pricing';
import Step7Logo from './Step7Logo';
import Step8Verification from './Step8Verification';

const TOTAL_STEPS = 8;

// Estimated time per step (in minutes)
// Updated to reflect faster AI-powered scraping
const STEP_TIME_ESTIMATES = {
  0: 0.5, // Account creation
  1: 0.5, // Email verification
  2: 0.5, // Business basics (AI-powered scraping makes this much faster)
  3: 0.5, // Location
  4: 0.5, // Style
  5: 0.5, // Pricing
  6: 0.5, // Logo
  7: 0.5 // Verification
};

function SupplierRegistrationFlow() {
  const styles = useThemeStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, _setUser } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [formData, setFormData] = useState({});
  const [businessId, setBusinessId] = useState(null);
  const [_loading, _setLoading] = useState(false);
  
  // Extract referral code and add-another flag from URL
  const referralCode = new URLSearchParams(location.search).get('ref');
  const addAnother = new URLSearchParams(location.search).get('add') === '1';
  
  // Get business context if available (for logged-in suppliers)
  const businessContext = useContext(BusinessContext);
  const refreshBusinesses = businessContext?.refreshBusinesses;
  const selectBusiness = businessContext?.selectBusiness;

  // Check if user is already logged in as a supplier (verified)
  const isLoggedInSupplier = user && user.role === 'supplier' && user.isEmailVerified;
  const START_STEP = isLoggedInSupplier ? 2 : 0;

  // DB-driven step: derive initial step from auth + API, no localStorage
  const [stepResolved, setStepResolved] = useState(false);
  useEffect(() => {
    if (authLoading) return;

    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');

    // No token -> step 0 (Account creation)
    if (!token || userType !== 'supplier') {
      setActiveStep(0);
      setCompletedSteps(new Set());
      setStepResolved(true);
      return;
    }

    // Token but not verified -> step 1 (Verification)
    if (!user?.isEmailVerified) {
      setActiveStep(1);
      setCompletedSteps(new Set([0]));
      setFormData(prev => (user ? {
        ...prev,
        email: user.email,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        userId: user.id || user._id,
        verified: false
      } : prev));
      setStepResolved(true);
      return;
    }

    // Token + verified -> check DB for businesses
    api.get('/api/supplier/my-profile')
      .then(res => {
        const businesses = res.data?.businesses || [];
        if (businesses.length > 0 && !addAnother) {
          navigate('/supplier/dashboard', { replace: true });
        } else {
          setActiveStep(2);
          setCompletedSteps(new Set([0, 1]));
          setFormData(prev => (user ? {
            ...prev,
            email: user.email,
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            userId: user.id || user._id,
            verified: true
          } : prev));
        }
        setStepResolved(true);
      })
      .catch(() => {
        setActiveStep(2);
        setCompletedSteps(new Set([0, 1]));
        setFormData(prev => (user ? {
          ...prev,
          email: user.email,
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          userId: user.id || user._id,
          verified: true
        } : prev));
        setStepResolved(true);
      });
  }, [authLoading, user, navigate, addAnother]);

  const handleNext = async (stepData, newBusinessId) => {
    const updatedData = { ...formData, ...stepData };
    setFormData(updatedData);

    if (newBusinessId) {
      setBusinessId(newBusinessId);
    }

    const nextStep = activeStep + 1;
    setActiveStep(nextStep);
    setCompletedSteps(new Set([...completedSteps, activeStep]));

    // If we've completed all steps, redirect
    if (nextStep >= TOTAL_STEPS) {
      const finalBusinessId = newBusinessId || businessId;

      if (isLoggedInSupplier && finalBusinessId) {
        localStorage.setItem('currentBusinessId', finalBusinessId);
        if (refreshBusinesses) await refreshBusinesses();
        if (selectBusiness) {
          await new Promise(resolve => setTimeout(resolve, 100));
          selectBusiness(finalBusinessId);
        }
        // When adding another business, send to verification for the new one so details save to the right business
        if (addAnother) {
          navigate(`/supplier/verification/${finalBusinessId}`);
        } else {
          navigate('/supplier/dashboard');
        }
      } else if (finalBusinessId) {
        navigate(`/supplier/verification/${finalBusinessId}`);
      } else {
        navigate('/supplier/dashboard');
      }
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      const prevStep = activeStep - 1;
      setActiveStep(prevStep);
      const updated = new Set(completedSteps);
      updated.delete(prevStep);
      setCompletedSteps(updated);
    }
  };

  const handleStepComplete = (stepData, newBusinessId) => {
    // Only proceed to next step - don't trigger any authentication state changes
    // This prevents automatic sign-in during registration flow
    handleNext(stepData, newBusinessId);
  };

  const calculateProgress = () => {
    // Adjust total steps based on whether user is logged in
    const effectiveTotalSteps = isLoggedInSupplier ? TOTAL_STEPS - 2 : TOTAL_STEPS;
    const effectiveCompletedSteps = isLoggedInSupplier 
      ? completedSteps.size - 2 + (activeStep < TOTAL_STEPS ? 1 : 0)
      : completedSteps.size + (activeStep < TOTAL_STEPS ? 1 : 0);
    return (effectiveCompletedSteps / effectiveTotalSteps) * 100;
  };

  const calculateTimeRemaining = () => {
    let remaining = 0;
    // Add time for current step
    remaining += STEP_TIME_ESTIMATES[activeStep] || 0;
    // Add time for remaining steps (skip steps 0 and 1 if logged in)
    const startIndex = isLoggedInSupplier ? Math.max(activeStep + 1, 2) : activeStep + 1;
    for (let i = startIndex; i < TOTAL_STEPS; i++) {
      remaining += STEP_TIME_ESTIMATES[i] || 0;
    }
    return remaining;
  };

  const formatTimeEstimate = (minutes) => {
    if (minutes < 1) {
      return 'less than a minute';
    } else if (minutes === 1) {
      return 'about 1 minute';
    } else {
      return `about ${Math.ceil(minutes)} minutes`;
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Step1Account
            formData={formData}
            referralCode={referralCode}
            onComplete={(data) => handleStepComplete(data)}
            onError={_error => {}}
          />
        );
      case 1:
        return (
          <Step2Verification
            formData={formData}
            onComplete={(data) => handleStepComplete(data)}
            onError={_error => {}}
          />
        );
      case 2:
        return (
          <Step3BusinessBasics
            formData={formData}
            businessId={businessId}
            onComplete={(data, id) => handleStepComplete(data, id)}
            onError={_error => {}}
          />
        );
      case 3:
        return (
          <Step4Location
            formData={formData}
            businessId={businessId}
            onComplete={(data) => handleStepComplete(data)}
            onError={_error => {}}
          />
        );
      case 4:
        return (
          <Step5Style
            formData={formData}
            businessId={businessId}
            onComplete={(data) => handleStepComplete(data)}
            onError={_error => {}}
          />
        );
      case 5:
        return (
          <Step6Pricing
            formData={formData}
            businessId={businessId}
            onComplete={(data) => handleStepComplete(data)}
            onError={_error => {}}
          />
        );
      case 6:
        return (
          <Step7Logo
            formData={formData}
            businessId={businessId}
            onComplete={(data) => handleStepComplete(data)}
            onError={_error => {}}
          />
        );
      case 7:
        return (
          <Step8Verification
            formData={formData}
            businessId={businessId}
            onComplete={(data) => handleStepComplete(data)}
            onError={_error => {}}
          />
        );
      default:
        return null;
    }
  };

  const timeRemaining = calculateTimeRemaining();

  if (!stepResolved) {
    return (
      <Container maxWidth="md" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="body1">Loading...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: 4, ...styles.card.default }}>
        {/* Progress Indicator */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" sx={{ ...styles.text.secondary, fontWeight: 500 }}>
              {Math.round(calculateProgress())}% Complete
            </Typography>
            <Typography variant="body2" sx={{ ...styles.text.secondary, color: 'primary.main' }}>
              {timeRemaining > 0 ? `${formatTimeEstimate(timeRemaining)} remaining` : 'Almost done!'}
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={calculateProgress()} 
            sx={{ 
              height: 8, 
              borderRadius: 4,
              backgroundColor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                backgroundColor: 'primary.main'
              }
            }}
          />
        </Box>

        {/* Step Content */}
        <Box sx={{ minHeight: '400px' }}>
          {renderStepContent()}
        </Box>

        {/* Navigation */}
        {activeStep > START_STEP && activeStep < TOTAL_STEPS && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 4 }}>
            <Button
              onClick={handleBack}
              disabled={activeStep === START_STEP}
              variant="outlined"
              sx={{ color: 'text.secondary' }}
            >
              ← Back
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
}

export default SupplierRegistrationFlow;

